// src/Charts.js
// StomataStorage — 品種・処理区別の気孔応答分析
// Firestore は読み取り専用。既存の collection(db, user.email) を使用。
import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { Navigate } from "react-router-dom";
import { Column, Line, Scatter } from "@ant-design/plots";
import {
  Alert, Button, Card, Col, DatePicker, Empty, Row, Select,
  Space, Statistic, Switch, Table, Tabs, Tag, Typography, theme,
} from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { auth, db } from "./FirebaseConfig";
import StomataLoader from "./StomataLoader";
import "./App.css";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const COLORS = [
  "#1677ff", "#52c41a", "#fa8c16", "#9254de", "#eb2f96",
  "#13a8a8", "#faad14", "#2f54eb", "#8c8c8c", "#d4380d",
];

// 記述統計の指標。開気孔率は平均開度とは異なる独立の指標。
const METRICS = {
  apertureMean: { ja: "平均気孔開度", en: "Mean stomatal aperture", unit: "µm", field: "aperture", method: "mean" },
  apertureMedian: { ja: "気孔開度の中央値", en: "Median stomatal aperture", unit: "µm", field: "aperture", method: "median" },
  openRate: { ja: "開気孔率", en: "Open stomata rate", unit: "%", field: null, method: "rate" },
  areaMean: { ja: "平均開口面積", en: "Mean pore area", unit: "µm²", field: "area", method: "mean" },
  ratioMean: { ja: "平均短長軸比", en: "Mean pore aspect ratio", unit: "", field: "ratio", method: "mean" },
};
const ENV_FIELDS = {
  temperature: { ja: "気温", en: "Air temperature", unit: "°C", step: 1 },
  humidity: { ja: "相対湿度", en: "Relative humidity", unit: "%", step: 5 },
  vpd: { ja: "VPD", en: "VPD", unit: "kPa", step: 0.1 },
  leafTemperature: { ja: "葉温", en: "Leaf temperature", unit: "°C", step: 1 },
};
const WORDS = {
  ja: {
    title: "品種別 気孔応答分析", subtitle: "品種・処理区・時間帯・環境応答を比較",
    conditions: "分析条件", period: "撮影期間", cultivars: "比較する品種", treatments: "処理区の絞り込み",
    metric: "分析指標", groupBy: "比較単位", byCultivar: "品種別", byTreatment: "処理区別",
    includeClosed: "Closedを開度・面積の集計に含める", interval: "時間間隔", reset: "条件をリセット",
    records: "測定データCSV", clockCsv: "時間帯集計CSV", statsCsv: "比較統計CSV",
    varieties: "対象品種", nRecords: "対象レコード", analyzed: "主指標の集計対象", openRate: "全体の開気孔率",
    summary: "気孔開度の比較", measured: "Detected stomata", rate: "Open率", n: "解析対象 n",
    median: "中央値", mean: "平均", missing: "データなし", noData: "選択した条件に該当するデータがありません。",
    dual: "Open率・開度の同時推移", dualTitle: "Open率と平均気孔開度の推移", dualRate: "開気孔率の推移 (%)", dualAperture: "平均気孔開度の推移 (µm)", dualNote: "上段はOpen率、下段は平均開口長です。品種・処理区と集計間隔は共通です。各点はその時間帯に含まれる測定値から計算します。", dualCsv: "同時推移CSV", clock: "時間帯別比較", timeline: "撮影日時別", environment: "環境応答", distribution: "分布・統計",
    clockTitle: "時刻別の気孔応答", timelineTitle: "撮影日時に沿った気孔応答",
    groupMean: "群ごとの代表値", periodTitle: "午前・午後の比較", histTitle: "気孔測定値の分布",
    statsTitle: "群ごとの記述統計", envTitle: "環境条件と気孔応答の関係",
    xClock: "時刻", xTime: "撮影日時", envAxis: "横軸の環境項目", colorMode: "色分け",
    morningAfternoon: "午前・午後", recordsAvailable: "環境データを持つ件数", noEnv: "該当する環境データがありません。",
    warningTitle: "研究・解析上の注意", incompleteDate: "撮影日時を解釈できないデータは時系列比較から除外しました。",
    openStatusNote: "開気孔率は Open / (Open + Closed) × 100 で算出し、Closedを含むスイッチの影響を受けません。",
    noStatus: "Open/Closed判定がないレコードは開気孔率の分母から除外します。",
    estimated: "古い記録のうち明示的な判定がないものは、開口長 > 0 をOpenとして推定しています。",
    meanCaution: "平均・中央値は対象測定レコードの値です。気孔が同一画像・葉・株に由来する場合、レコード数は独立した生物学的反復数ではありません。",
    expCaution: "品種差を検証するには、採取時刻・環境条件・葉位・測定面を揃え、画像／株を反復単位として統計解析してください。この画面では有意差検定はしていません。",
    metricData: "この指標の測定値がありません。", histOpen: "開気孔率を選択しているため、分布図には開口長（µm）の分布を表示しています。",
    noTime: "時系列グラフ用の有効日時がありません。", timeLabel: "測定値は選択した時間間隔ごとに集計しています。",
    treatmentUnset: "処理区未設定", cultivarUnset: "品種未設定", morning: "午前", afternoon: "午後",
    noteRead: "既存Firestoreデータは読み取りのみで、保存形式や他画面は変更しません。",
  },
  en: {
    title: "Stomatal Response by Variety", subtitle: "Compare varieties, treatments, time of day and environment",
    conditions: "Analysis filters", period: "Date range", cultivars: "Varieties", treatments: "Treatments",
    metric: "Metric", groupBy: "Compare by", byCultivar: "Variety", byTreatment: "Treatment",
    includeClosed: "Include closed stomata in aperture/area statistics", interval: "Time bin", reset: "Reset",
    records: "Records CSV", clockCsv: "Time-bin CSV", statsCsv: "Statistics CSV",
    varieties: "Varieties", nRecords: "Records", analyzed: "Metric sample size", openRate: "Overall open rate",
    summary: "Stomatal aperture comparison", measured: "Detected stomata", rate: "Open rate", n: "Analyzed n",
    median: "Median", mean: "Mean", missing: "No data", noData: "No data for the current filters.",
    dual: "Open rate & aperture", dualTitle: "Open rate and mean aperture over time", dualRate: "Open rate over time (%)", dualAperture: "Mean aperture over time (µm)", dualNote: "Top: Open rate; bottom: mean aperture. Groups and time bins are shared. Each point aggregates measurements within that time bin.", dualCsv: "Dual trends CSV", clock: "By time of day", timeline: "Chronological", environment: "Environment", distribution: "Distributions & stats",
    clockTitle: "Stomatal response by time of day", timelineTitle: "Chronological stomatal response",
    groupMean: "Group summary", periodTitle: "AM / PM comparison", histTitle: "Distribution of measurements",
    statsTitle: "Descriptive statistics", envTitle: "Environmental response",
    xClock: "Time of day", xTime: "Date and time", envAxis: "Environmental variable", colorMode: "Color by",
    morningAfternoon: "AM / PM", recordsAvailable: "Records with environment data", noEnv: "No environment data available.",
    warningTitle: "Interpretation notes", incompleteDate: "Records with invalid timestamps are excluded from time-based charts.",
    openStatusNote: "Open rate = Open / (Open + Closed) × 100; the include-Closed switch does not affect this rate.",
    noStatus: "Records without a valid open/closed classification are excluded from this denominator.",
    estimated: "For legacy records without explicit classification, aperture > 0 is treated as Open (estimated).",
    meanCaution: "Mean and median summarize measurement records, not necessarily independent biological replicates.",
    expCaution: "For variety comparisons, control time, environment, leaf position and leaf side, and analyze image/plant replicates. This page does not test significance.",
    metricData: "No values for this metric.", histOpen: "With Open rate selected, the histogram shows aperture (µm) rather than 0/100 classifications.",
    noTime: "No valid timestamps available.", timeLabel: "Measurements are aggregated in the selected time bins.",
    treatmentUnset: "Treatment unspecified", cultivarUnset: "Variety unspecified", morning: "AM", afternoon: "PM",
    noteRead: "This page reads existing Firestore documents; it does not modify them.",
  },
};

function first(obj, keys) {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return null;
}
function numberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}
function milliseconds(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "object" && typeof value.seconds === "number") {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  if (typeof value === "number") return value < 1e11 ? value * 1000 : value;
  if (typeof value === "string" && /^\d{10,13}$/.test(value)) {
    const n = Number(value); return value.length === 10 ? n * 1000 : n;
  }
  const parsed = moment(value, [moment.ISO_8601, "YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss",
    "YY/MM/DD HH:mm:ss", "YYYY/MM/DD HH:mm", "YYYY-MM-DD HH:mm"], true);
  return parsed.isValid() ? parsed.valueOf() : null;
}
function detectStatus(data, aperture) {
  const booleanValue = first(data, ["isOpen", "open"]);
  if (typeof booleanValue === "boolean") return { isOpen: booleanValue, estimated: false };
  if (booleanValue === 1 || booleanValue === 0) return { isOpen: booleanValue === 1, estimated: false };
  const raw = first(data, ["openClose", "status", "state", "stomataStatus", "classification"]);
  const text = String(raw ?? "").toLowerCase().trim();
  if (["open", "opened", "true", "1", "開", "開口"].includes(text)) return { isOpen: true, estimated: false };
  if (["closed", "close", "false", "0", "閉", "閉鎖"].includes(text)) return { isOpen: false, estimated: false };
  if (aperture !== null) return { isOpen: aperture > 0, estimated: true };
  return { isOpen: null, estimated: false };
}
function normalize(docSnap, words) {
  const data = docSnap.data();
  const aperture = numberOrNull(first(data, ["aperture", "stomatalAperture", "openingLength"]));
  const status = detectStatus(data, aperture);
  return {
    id: docSnap.id,
    timestampMs: milliseconds(first(data, ["timestamp", "capturedAt", "captureTime", "shootingDate", "date", "createdAt"])),
    cultivar: String(first(data, ["cultiverName", "cultivarName", "varietyName", "cultivar", "variety"]) ?? words.cultivarUnset).trim() || words.cultivarUnset,
    treatment: String(first(data, ["treatment", "treatmentName", "condition", "group", "groupName"]) ?? words.treatmentUnset).trim() || words.treatmentUnset,
    aperture, area: numberOrNull(first(data, ["poreArea", "apertureArea", "area", "pore_area"])),
    ratio: numberOrNull(first(data, ["poreAspectRatio", "aspectRatio", "shortLongRatio", "pore_aspect_ratio"])),
    temperature: numberOrNull(first(data, ["temperature", "airTemperature", "temp"])),
    humidity: numberOrNull(first(data, ["humidity", "relativeHumidity", "rh"])),
    vpd: numberOrNull(first(data, ["vpd", "VPD"])),
    leafTemperature: numberOrNull(first(data, ["leafTemperature", "leafTemp"])),
    ...status,
  };
}
function mean(values) { return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null; }
function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}
function sampleSd(values) {
  if (values.length < 2) return null;
  const avg = mean(values);
  return Math.sqrt(values.reduce((sum, n) => sum + (n - avg) ** 2, 0) / (values.length - 1));
}
function fixed(value, digits = 2) {
  return value === null || value === undefined || !Number.isFinite(value) ? "—" : Number(value).toFixed(digits);
}
function valueFor(record, field, includeClosed) {
  if (!field) return null;
  if (!includeClosed && record.isOpen === false) return null;
  const v = record[field];
  if (!Number.isFinite(v)) return null;
  // 過去の Open 0.0µm 記録は開口長の集計対象外（Closed の0は含む設定時のみ対象）。
  if (field === "aperture" && record.isOpen === true && v === 0) return null;
  return v;
}
function groupStats(items, metric, includeClosed) {
  const known = items.filter((r) => r.isOpen !== null);
  const open = known.filter((r) => r.isOpen === true).length;
  const closed = known.length - open;
  const apertureValues = items.map((r) => valueFor(r, "aperture", includeClosed)).filter((v) => v !== null);
  const field = METRICS[metric].field;
  const fieldValues = field
    ? items.map((r) => valueFor(r, field, includeClosed)).filter((v) => v !== null)
    : [];
  const rate = known.length ? (open / known.length) * 100 : null;
  const value = metric === "openRate" ? rate
    : METRICS[metric].method === "median" ? median(fieldValues) : mean(fieldValues);
  return {
    detected: items.length, classified: known.length, open, closed, rate,
    estimated: items.filter((r) => r.estimated).length,
    nAperture: apertureValues.length,
    apertureMean: mean(apertureValues), apertureMedian: median(apertureValues),
    value, metricN: metric === "openRate" ? known.length : fieldValues.length,
    metricMean: mean(fieldValues), metricMedian: median(fieldValues),
    metricSd: sampleSd(fieldValues),
    metricMin: fieldValues.length ? fieldValues.reduce((a, b) => Math.min(a, b), Infinity) : null,
    metricMax: fieldValues.length ? fieldValues.reduce((a, b) => Math.max(a, b), -Infinity) : null,
  };
}
function localBucket(ms, minutes) {
  const d = new Date(ms);
  d.setMinutes(Math.floor(d.getMinutes() / minutes) * minutes, 0, 0);
  return d.getTime();
}
function clockBucket(ms, minutes) {
  const d = new Date(ms);
  return Math.floor((d.getHours() * 60 + d.getMinutes()) / minutes) * minutes;
}
function clockLabel(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}
function groupAggregates(items, metric, includeClosed, groupMode, bucketFn, labelFn) {
  const map = new Map();
  for (const item of items) {
    const series = groupMode === "treatment" ? item.treatment : item.cultivar;
    const bucket = bucketFn(item);
    const key = JSON.stringify([series, bucket]);
    if (!map.has(key)) map.set(key, { series, bucket, records: [] });
    map.get(key).records.push(item);
  }
  return [...map.values()].map((g) => {
    const s = groupStats(g.records, metric, includeClosed);
    return { series: g.series, bucket: g.bucket, x: labelFn(g.bucket), value: s.value, n: s.metricN,
      open: s.open, closed: s.closed, total: g.records.length };
  }).filter((d) => d.value !== null)
    .sort((a, b) => a.bucket - b.bucket || a.series.localeCompare(b.series));
}
function csvDownload(filename, rows) {
  const escape = (value) => {
    const original = value === null || value === undefined ? "" : String(value);
    // Excel のCSV数式インジェクションを抑止（数値データはそのまま）。
    const safe = typeof value === "string" && /^[\s]*[=+@\-\t\r]/.test(original) ? `'${original}` : original;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const content = "\uFEFF" + rows.map((row) => row.map(escape).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Charts() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "ja").toLowerCase().startsWith("ja") ? "ja" : "en";
  const w = WORDS[lang];
  const { token: { colorBgContainer } } = theme.useToken();
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [posts, setPosts] = useState([]);
  const [invalidDates, setInvalidDates] = useState(0);
  const [dateRange, setDateRange] = useState([]);
  const [selectedCultivars, setSelectedCultivars] = useState([]);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [metric, setMetric] = useState("apertureMean");
  const [groupMode, setGroupMode] = useState("cultivar");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [environment, setEnvironment] = useState("temperature");
  const [scatterColorMode, setScatterColorMode] = useState("series");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (person) => {
      setUser(person);
      setAuthReady(true);
      if (!person) { setPosts([]); setLoading(false); }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    let active = true;
    setLoading(true); setLoadError("");
    getDocs(collection(db, user.email))
      .then((snapshot) => {
        if (!active) return;
        const normalized = snapshot.docs.map((docSnap) => normalize(docSnap, w));
        const valid = normalized.filter((r) => Number.isFinite(r.timestampMs));
        valid.sort((a, b) => a.timestampMs - b.timestampMs);
        setPosts(valid);
        setInvalidDates(normalized.length - valid.length);
        setSelectedCultivars([...new Set(valid.map((r) => r.cultivar))].sort());
        setSelectedTreatments([...new Set(valid.map((r) => r.treatment))].sort());
      })
      .catch((e) => {
        if (!active) return;
        console.error("StomataStorage Charts fetch error:", e);
        setPosts([]); setLoadError(lang === "ja" ? "データ取得に失敗しました。接続とFirestore権限を確認してください。" : "Could not load Firestore data.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user?.uid, user?.email]);

  const allCultivars = useMemo(() => [...new Set(posts.map((r) => r.cultivar))].sort(), [posts]);
  const allTreatments = useMemo(() => [...new Set(posts.map((r) => r.treatment))].sort(), [posts]);
  const filtered = useMemo(() => {
    const start = dateRange?.[0]?.clone().startOf("day").valueOf() ?? -Infinity;
    const end = dateRange?.[1]?.clone().endOf("day").valueOf() ?? Infinity;
    const cultivars = new Set(selectedCultivars);
    const treatments = new Set(selectedTreatments);
    return posts.filter((r) => r.timestampMs >= start && r.timestampMs <= end &&
      cultivars.has(r.cultivar) && treatments.has(r.treatment));
  }, [posts, selectedCultivars, selectedTreatments, dateRange]);
  const metricInfo = METRICS[metric];
  const metricLabel = `${metricInfo[lang]}${metricInfo.unit ? ` (${metricInfo.unit})` : ""}`;
  const envInfo = ENV_FIELDS[environment];
  const byName = useMemo(() => {
    const m = new Map();
    for (const r of filtered) {
      const label = groupMode === "treatment" ? r.treatment : r.cultivar;
      if (!m.has(label)) m.set(label, []);
      m.get(label).push(r);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupMode]);
  const summary = useMemo(() => byName.map(([name, items]) => ({
    key: name, name, ...groupStats(items, metric, includeClosed),
  })), [byName, metric, includeClosed]);
  const overall = useMemo(() => groupStats(filtered, metric, includeClosed), [filtered, metric, includeClosed]);
  const clockData = useMemo(() => groupAggregates(filtered, metric, includeClosed, groupMode,
    (r) => clockBucket(r.timestampMs, intervalMinutes), clockLabel)
    .map((r) => ({ ...r, time: r.x })), [filtered, metric, includeClosed, groupMode, intervalMinutes]);
  const timelineData = useMemo(() => groupAggregates(filtered, metric, includeClosed, groupMode,
    (r) => localBucket(r.timestampMs, intervalMinutes), (n) => moment(n).format("YYYY-MM-DD HH:mm"))
    .map((r) => ({ ...r, time: r.x })), [filtered, metric, includeClosed, groupMode, intervalMinutes]);
  // 2指標を同じ時間帯で独立集計。Open率はClosed切替に影響されない。
  const dualClock = useMemo(() => ({
    rate: groupAggregates(filtered, "openRate", includeClosed, groupMode,
      (r) => clockBucket(r.timestampMs, intervalMinutes), clockLabel).map((r) => ({ ...r, time: r.x })),
    aperture: groupAggregates(filtered, "apertureMean", includeClosed, groupMode,
      (r) => clockBucket(r.timestampMs, intervalMinutes), clockLabel).map((r) => ({ ...r, time: r.x })),
  }), [filtered, includeClosed, groupMode, intervalMinutes]);
  const dualTimeline = useMemo(() => ({
    rate: groupAggregates(filtered, "openRate", includeClosed, groupMode,
      (r) => localBucket(r.timestampMs, intervalMinutes), (n) => moment(n).format("YYYY-MM-DD HH:mm"))
      .map((r) => ({ ...r, time: r.x })),
    aperture: groupAggregates(filtered, "apertureMean", includeClosed, groupMode,
      (r) => localBucket(r.timestampMs, intervalMinutes), (n) => moment(n).format("YYYY-MM-DD HH:mm"))
      .map((r) => ({ ...r, time: r.x })),
  }), [filtered, includeClosed, groupMode, intervalMinutes]);
  const periodData = useMemo(() => {
    const m = new Map();
    for (const item of filtered) {
      const name = groupMode === "treatment" ? item.treatment : item.cultivar;
      const period = new Date(item.timestampMs).getHours() < 12 ? w.morning : w.afternoon;
      const key = JSON.stringify([name, period]);
      if (!m.has(key)) m.set(key, { name, period, items: [] });
      m.get(key).items.push(item);
    }
    return [...m.values()].map((g) => ({
      series: g.name, period: g.period, value: groupStats(g.items, metric, includeClosed).value,
    })).filter((r) => r.value !== null);
  }, [filtered, groupMode, metric, includeClosed, w]);
  // 環境応答：開気孔率は個体ごとの0/100点ではなく環境値を区切って集計する。
  const scatterData = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const x = r[environment];
      if (!Number.isFinite(x)) continue;
      const bin = Math.round(x / envInfo.step) * envInfo.step;
      const roundedBin = Number(bin.toFixed(4));
      const series = scatterColorMode === "period"
        ? (new Date(r.timestampMs).getHours() < 12 ? w.morning : w.afternoon)
        : (groupMode === "treatment" ? r.treatment : r.cultivar);
      const key = JSON.stringify([series, roundedBin]);
      if (!map.has(key)) map.set(key, { series, x: roundedBin, items: [] });
      map.get(key).items.push(r);
    }
    return [...map.values()].map((g) => ({
      series: g.series, x: g.x, value: groupStats(g.items, metric, includeClosed).value,
      n: g.items.length,
    })).filter((r) => r.value !== null).sort((a, b) => a.x - b.x);
  }, [filtered, environment, envInfo.step, scatterColorMode, groupMode, metric, includeClosed, w]);
  const histogram = useMemo(() => {
    const field = metricInfo.field || "aperture";
    const valid = filtered.map((r) => ({
      series: groupMode === "treatment" ? r.treatment : r.cultivar,
      value: valueFor(r, field, includeClosed),
    })).filter((r) => r.value !== null);
    if (!valid.length) return [];
    const values = valid.map((r) => r.value);
    const lo = values.reduce((a, b) => Math.min(a, b), Infinity);
    const hi = values.reduce((a, b) => Math.max(a, b), -Infinity);
    const nBins = lo === hi ? 1 : 12;
    const width = nBins === 1 ? 1 : (hi - lo) / nBins;
    const totals = new Map(), counts = new Map();
    for (const r of valid) {
      const index = Math.min(nBins - 1, Math.floor((r.value - lo) / width));
      const key = JSON.stringify([r.series, index]);
      counts.set(key, (counts.get(key) || 0) + 1);
      totals.set(r.series, (totals.get(r.series) || 0) + 1);
    }
    const out = [];
    for (const series of totals.keys()) for (let i = 0; i < nBins; i++) {
      const count = counts.get(JSON.stringify([series, i])) || 0;
      out.push({ series, index: i,
        bin: nBins === 1 ? fixed(lo) : `${fixed(lo + i * width)}–${fixed(lo + (i + 1) * width)}`,
        count, percent: count * 100 / totals.get(series) });
    }
    return out.sort((a, b) => a.index - b.index || a.series.localeCompare(b.series));
  }, [filtered, groupMode, metricInfo.field, includeClosed]);

  const reset = () => {
    setDateRange([]); setSelectedCultivars(allCultivars); setSelectedTreatments(allTreatments);
    setMetric("apertureMean"); setGroupMode("cultivar"); setIncludeClosed(false);
    setIntervalMinutes(60); setEnvironment("temperature"); setScatterColorMode("series");
  };
  const stamp = moment().format("YYYYMMDD_HHmmss");
  const exportRecords = () => csvDownload(`StomataStorage_records_${stamp}.csv`, [
    ["id", "local_timestamp", "cultivar", "treatment", "aperture_um", "area_um2", "aspect_ratio", "status", "status_inferred", "temperature_C", "humidity_pct", "vpd_kPa", "leaf_temperature_C"],
    ...filtered.map((r) => [r.id, moment(r.timestampMs).format("YYYY-MM-DD HH:mm:ss"), r.cultivar, r.treatment,
      r.aperture, r.area, r.ratio, r.isOpen === null ? "Unknown" : r.isOpen ? "Open" : "Closed",
      r.estimated, r.temperature, r.humidity, r.vpd, r.leafTemperature]),
  ]);
  const exportClock = () => csvDownload(`StomataStorage_clock_${stamp}.csv`, [
    ["time_local", "compare_by", "series", "metric", "value", "n", "open", "closed", "total_records", "interval_min", "include_closed_for_aperture_area"],
    ...clockData.map((r) => [r.time, groupMode, r.series, metric, r.value, r.n,
      r.open, r.closed, r.total, intervalMinutes, includeClosed]),
  ]);
  const exportStats = () => csvDownload(`StomataStorage_stats_${stamp}.csv`, [
    ["compare_by", "series", "metric", "metric_value", "metric_n", "detected_records", "known_status", "open", "closed", "open_rate_pct", "inferred_status", "aperture_n", "aperture_mean_um", "aperture_median_um", "metric_mean", "metric_median", "metric_sd", "metric_min", "metric_max", "include_closed_for_aperture_area"],
    ...summary.map((r) => [groupMode, r.name, metric, r.value, r.metricN, r.detected, r.classified,
      r.open, r.closed, r.rate, r.estimated, r.nAperture, r.apertureMean, r.apertureMedian,
      r.metricMean, r.metricMedian, r.metricSd, r.metricMin, r.metricMax, includeClosed]),
  ]);

  const exportDual = () => {
    const merged = new Map();
    for (const [kind, rows] of [["rate", dualClock.rate], ["aperture", dualClock.aperture]]) {
      for (const r of rows) {
        const key = JSON.stringify([r.series, r.bucket]);
        if (!merged.has(key)) merged.set(key, { time: r.time, series: r.series });
        const entry = merged.get(key);
        entry[kind] = r.value;
        entry[`${kind}N`] = r.n;
        if (kind === "rate") { entry.open = r.open; entry.closed = r.closed; }
      }
    }
    csvDownload(`StomataStorage_open_rate_aperture_${stamp}.csv`, [
      ["time_local", "series", "open_rate_pct", "open_rate_n", "open", "closed", "mean_aperture_um", "aperture_n", "interval_min", "include_closed_for_aperture"],
      ...[...merged.values()].sort((a, b) => a.time.localeCompare(b.time) || a.series.localeCompare(b.series))
        .map((r) => [r.time, r.series, r.rate, r.rateN, r.open, r.closed, r.aperture, r.apertureN, intervalMinutes, includeClosed]),
    ]);
  };
  const dualLineConfig = (max) => ({
    height: 310, autoFit: true, xField: "time", yField: "value", seriesField: "series",
    color: COLORS, smooth: false, point: { size: 3 },
    legend: { position: "bottom", flipPage: true },
    yAxis: { min: 0, max, nice: max === undefined },
    tooltip: { shared: true, showMarkers: true },
  });
  const commonLine = {
    height: 400, autoFit: true, yField: "value", seriesField: "series", color: COLORS,
    smooth: false, point: { size: 3 }, legend: { position: "bottom", flipPage: true },
    yAxis: { min: 0, max: metric === "openRate" ? 100 : undefined, nice: metric !== "openRate", title: { text: metricLabel } },
    tooltip: { shared: true, showMarkers: true },
  };
  const tableColumns = [
    { title: groupMode === "treatment" ? w.treatments : w.cultivars, dataIndex: "name", key: "name", fixed: "left", width: 140 },
    { title: w.measured, dataIndex: "detected", key: "detected", width: 120 },
    { title: "Open", dataIndex: "open", key: "open", width: 85 },
    { title: "Closed", dataIndex: "closed", key: "closed", width: 85 },
    { title: `${w.rate} (%)`, dataIndex: "rate", key: "rate", render: (v) => fixed(v, 1), width: 110 },
    { title: `${w.mean} (µm)`, dataIndex: "apertureMean", key: "apertureMean", render: fixed, width: 105 },
    { title: `${w.median} (µm)`, dataIndex: "apertureMedian", key: "apertureMedian", render: fixed, width: 105 },
    { title: metricLabel, dataIndex: "value", key: "value", render: (v) => fixed(v, metric === "openRate" ? 1 : 2), width: 160 },
    { title: w.n, dataIndex: "metricN", key: "metricN", width: 105 },
    { title: "SD", dataIndex: "metricSd", key: "metricSd", render: fixed, width: 95 },
  ];
  const chartCard = (title, chart, note) => <Card title={title} style={{ height: "100%" }}>
    {note && <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>{note}</Text>}
    {chart}
  </Card>;
  const noMetricData = <Empty description={w.metricData} />;

  if (!authReady || loading) return <div className="spin"><StomataLoader /></div>;
  if (!user) return <Navigate to="/" />;

  return (
    <div style={{ padding: 20, background: colorBgContainer, minHeight: "100%" }}>
      <Title level={3} style={{ textAlign: "center", marginBottom: 4 }}>{w.title}</Title>
      <p style={{ textAlign: "center", color: "#777", marginBottom: 24 }}>{w.subtitle} — StomataStorage</p>

      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
      {invalidDates > 0 && <Alert type="warning" showIcon style={{ marginBottom: 16 }}
        message={`${w.incompleteDate} (${invalidDates})`} />}

      <Card title={w.conditions} style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap size="middle" align="end">
            <div><Text strong style={{ display: "block", marginBottom: 6 }}>{w.period}</Text>
              <RangePicker value={dateRange} onChange={(dates) => setDateRange(dates || [])} format="YYYY/MM/DD" allowClear />
            </div>
            <div><Text strong style={{ display: "block", marginBottom: 6 }}>{w.cultivars}</Text>
              <Select mode="multiple" allowClear maxTagCount="responsive" value={selectedCultivars}
                onChange={setSelectedCultivars} options={allCultivars.map((v) => ({ value: v, label: v }))}
                style={{ minWidth: 200, maxWidth: 450 }} placeholder={w.cultivars} />
            </div>
            <div><Text strong style={{ display: "block", marginBottom: 6 }}>{w.treatments}</Text>
              <Select mode="multiple" allowClear maxTagCount="responsive" value={selectedTreatments}
                onChange={setSelectedTreatments} options={allTreatments.map((v) => ({ value: v, label: v }))}
                style={{ minWidth: 175, maxWidth: 350 }} placeholder={w.treatments} />
            </div>
            <div><Text strong style={{ display: "block", marginBottom: 6 }}>{w.groupBy}</Text>
              <Select value={groupMode} onChange={setGroupMode} style={{ minWidth: 140 }} options={[
                { value: "cultivar", label: w.byCultivar }, { value: "treatment", label: w.byTreatment },
              ]} />
            </div>
            <div><Text strong style={{ display: "block", marginBottom: 6 }}>{w.metric}</Text>
              <Select value={metric} onChange={setMetric} style={{ minWidth: 235 }} options={Object.entries(METRICS).map(([value, info]) => ({
                value, label: `${info[lang]}${info.unit ? ` (${info.unit})` : ""}`,
              }))} />
            </div>
          </Space>
          <Space wrap size="large">
            <Space><Text>{w.includeClosed}</Text><Switch checked={includeClosed} onChange={setIncludeClosed} /></Space>
            <Space><Text>{w.interval}</Text><Select value={intervalMinutes} onChange={setIntervalMinutes} style={{ width: 110 }} options={[
              { value: 10, label: lang === "ja" ? "10分" : "10 min" },
              { value: 30, label: lang === "ja" ? "30分" : "30 min" },
              { value: 60, label: lang === "ja" ? "1時間" : "60 min" },
            ]} /></Space>
            <Button onClick={reset}>{w.reset}</Button>
          </Space>
          <Space wrap>
            <Button onClick={exportRecords} disabled={!filtered.length}>{w.records}</Button>
            <Button onClick={exportClock} disabled={!clockData.length}>{w.clockCsv}</Button>
            <Button onClick={exportStats} disabled={!summary.length}>{w.statsCsv}</Button>
          </Space>
          {metric === "openRate" && <Alert type="info" showIcon message={w.openStatusNote} />}
        </Space>
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 18 }}>
        <Col xs={12} md={6}><Card><Statistic title={w.varieties} value={new Set(filtered.map((r) => r.cultivar)).size} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={w.nRecords} value={filtered.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={w.analyzed} value={overall.metricN} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={w.openRate} value={overall.rate === null ? "—" : Number(overall.rate.toFixed(1))}
          suffix={overall.rate === null ? "" : "%"} /></Card></Col>
      </Row>

      <Title level={4} style={{ marginBottom: 4 }}>{w.summary}</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        {groupMode === "treatment" ? w.byTreatment : w.byCultivar} / {metricLabel}
      </Text>
      {summary.length ? <Row gutter={[14, 14]} style={{ marginBottom: 22 }}>
        {summary.map((item, index) => <Col xs={24} lg={12} key={item.key}>
          <Card style={{ borderTop: `4px solid ${COLORS[index % COLORS.length]}`, height: "100%" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>
              <span style={{ display: "inline-block", background: COLORS[index % COLORS.length], width: 10, height: 10,
                borderRadius: "50%", marginRight: 10 }} />{item.name}
            </div>
            <Row gutter={[12, 16]}>
              <Col span={8}><Statistic title={w.measured} value={item.detected} />
                <Text type="secondary" style={{ fontSize: 11 }}>Open {item.open} / Closed {item.closed}</Text></Col>
              <Col span={8}><Statistic title={w.rate} value={item.rate === null ? "—" : Number(item.rate.toFixed(1))}
                suffix={item.rate === null ? "" : "%"} /></Col>
              <Col span={8}><Statistic title={w.n} value={item.metricN} /></Col>
            </Row>
            <div style={{ borderTop: "1px solid #eee", margin: "14px 0" }} />
            <Row gutter={[12, 14]}>
              <Col span={12}><Statistic title={`${w.median} (µm)`} value={item.apertureMedian === null ? "—" : Number(item.apertureMedian.toFixed(2))} suffix={item.apertureMedian === null ? "" : "µm"} /></Col>
              <Col span={12}><Statistic title={`${w.mean} (µm)`} value={item.apertureMean === null ? "—" : Number(item.apertureMean.toFixed(2))} suffix={item.apertureMean === null ? "" : "µm"} /></Col>
            </Row>
            <div style={{ marginTop: 14 }}><Tag color="blue">{metricLabel}: {fixed(item.value, metric === "openRate" ? 1 : 2)} {metricInfo.unit}</Tag></div>
          </Card>
        </Col>)}
      </Row> : <Empty description={w.noData} style={{ marginBottom: 22 }} />}

      <Tabs defaultActiveKey="dual" items={[
        { key: "dual", label: w.dual, children: <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Card title={w.dualTitle} extra={<Button onClick={exportDual}>{w.dualCsv}</Button>}>
            <Text type="secondary">{w.dualNote}</Text>
          </Card>
          {chartCard(w.dualRate,
            dualClock.rate.length ? <Line {...dualLineConfig(100)} data={dualClock.rate}
              xAxis={{ title: { text: w.xClock } }} /> : noMetricData)}
          {chartCard(w.dualAperture,
            dualClock.aperture.length ? <Line {...dualLineConfig(undefined)} data={dualClock.aperture}
              xAxis={{ title: { text: w.xClock } }} /> : noMetricData)}
          <Card title={`${w.timeline} — ${w.dualRate}`}>
            {dualTimeline.rate.length ? <Line {...dualLineConfig(100)} data={dualTimeline.rate}
              xAxis={{ title: { text: w.xTime }, label: { autoRotate: true, autoHide: true } }} /> : noMetricData}
          </Card>
          <Card title={`${w.timeline} — ${w.dualAperture}`}>
            {dualTimeline.aperture.length ? <Line {...dualLineConfig(undefined)} data={dualTimeline.aperture}
              xAxis={{ title: { text: w.xTime }, label: { autoRotate: true, autoHide: true } }} /> : noMetricData}
          </Card>
        </Space> },
        { key: "clock", label: w.clock, children: <Row gutter={[16, 16]}>
          <Col span={24}>{chartCard(w.clockTitle,
            clockData.length ? <Line {...commonLine} data={clockData} xField="time"
              xAxis={{ title: { text: w.xClock } }} /> : noMetricData, w.timeLabel)}</Col>
          <Col span={24}>{chartCard(w.groupMean,
            summary.some((s) => s.value !== null) ? <Column
              data={summary.filter((s) => s.value !== null)} xField="name" yField="value" color={COLORS[0]}
              height={330} label={{ position: "top", formatter: (r) => fixed(r.value, 1) }}
              yAxis={{ min: 0, max: metric === "openRate" ? 100 : undefined, title: { text: metricLabel } }} />
              : noMetricData)}</Col>
        </Row> },
        { key: "timeline", label: w.timeline, children: chartCard(w.timelineTitle,
          timelineData.length ? <Line {...commonLine} data={timelineData} xField="time"
            xAxis={{ title: { text: w.xTime }, label: { autoRotate: true, autoHide: true } }} /> : noMetricData,
          w.timeLabel) },
        { key: "environment", label: w.environment, children: chartCard(w.envTitle, <>
          <Space wrap style={{ marginBottom: 16 }}>
            <Text>{w.envAxis}:</Text>
            <Select value={environment} onChange={setEnvironment} style={{ width: 185 }} options={Object.entries(ENV_FIELDS).map(([value, info]) => ({ value, label: `${info[lang]} (${info.unit})` }))} />
            <Text>{w.colorMode}:</Text>
            <Select value={scatterColorMode} onChange={setScatterColorMode} style={{ width: 165 }} options={[
              { value: "series", label: groupMode === "treatment" ? w.byTreatment : w.byCultivar },
              { value: "period", label: w.morningAfternoon },
            ]} />
            <Tag>{w.recordsAvailable}: {filtered.filter((r) => Number.isFinite(r[environment])).length}</Tag>
          </Space>
          {scatterData.length ? <Scatter data={scatterData} xField="x" yField="value"
            colorField="series" color={COLORS} size={4} height={390}
            legend={{ position: "bottom", flipPage: true }}
            xAxis={{ title: { text: `${envInfo[lang]} (${envInfo.unit})` } }}
            yAxis={{ min: 0, max: metric === "openRate" ? 100 : undefined, title: { text: metricLabel } }} />
            : <Empty description={w.noEnv} />}
          <Text type="secondary">{lang === "ja" ? "環境値を一定幅で集計した点です（気温・葉温1°C、湿度5%、VPD 0.1kPa）。" : "Points aggregate measurements in bins of 1°C, 5% RH or 0.1 kPa VPD."}</Text>
        </>) },
        { key: "distribution", label: w.distribution, children: <Row gutter={[16, 16]}>
          <Col span={24}>{chartCard(w.periodTitle,
            periodData.length ? <Column data={periodData} xField="series" yField="value" seriesField="period"
              isGroup color={["#1677ff", "#fa8c16"]} height={330}
              yAxis={{ min: 0, max: metric === "openRate" ? 100 : undefined, title: { text: metricLabel } }} />
              : noMetricData)}</Col>
          <Col span={24}>{chartCard(w.histTitle,
            histogram.length ? <Column data={histogram} xField="bin" yField="percent" seriesField="series"
              isGroup color={COLORS} height={390} legend={{ position: "bottom", flipPage: true }}
              xAxis={{ title: { text: metric === "openRate" ? "Stomatal Aperture (µm)" : metricLabel }, label: { autoRotate: true, autoHide: true } }}
              yAxis={{ min: 0, title: { text: lang === "ja" ? "各群内の割合 (%)" : "Within-group proportion (%)" } }} />
              : noMetricData, metric === "openRate" ? w.histOpen : null)}</Col>
          <Col span={24}>{chartCard(w.statsTitle,
            <Table size="small" rowKey="key" dataSource={summary} columns={tableColumns}
              scroll={{ x: 1100 }} pagination={{ pageSize: 15 }} />)}</Col>
        </Row> },
      ]} />

      <Alert type="info" showIcon style={{ marginTop: 20 }} message={w.warningTitle}
        description={<div>
          <p style={{ margin: "0 0 6px" }}>{w.openStatusNote}</p>
          <p style={{ margin: "0 0 6px" }}>{w.noStatus} {overall.estimated > 0 ? `${w.estimated} (${overall.estimated})` : ""}</p>
          <p style={{ margin: "0 0 6px" }}>{w.meanCaution}</p>
          <p style={{ margin: "0 0 6px" }}>{w.expCaution}</p>
          <p style={{ margin: 0 }}>{w.noteRead}</p>
        </div>} />
    </div>
  );
}
